// Client layer (TS-2.5.03). ProjectQuery's teamCodes/statusCodes (ProjectQuery.cs) name what they
// expect - reference CODES, not display labels, the same round-trip decisions.md D-199 documents
// for the roadmap filter bar - so these selects are built from useReferenceList("team") /
// useReferenceList("project.status") rather than a hardcoded vocabulary.
//
// Native <select multiple> has no built-in "clear all" (ctrl/cmd-click to deselect is
// undiscoverable), and there is no way to clear the name text and both multi-selects at once - the
// "Clear filters" button (rendered but disabled while no filter is active) is that affordance
// rather than a per-select "All teams"/"All statuses" option, which a multi-select doesn't need:
// no selection already means "all".
import type { ChangeEvent } from "react";
import type { ReferenceValue } from "../../api/generated/contracts";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { strings } from "../../strings";

export interface ProjectFiltersProps {
  readonly nameContains: string;
  readonly onNameContainsChange: (next: string) => void;
  readonly teams: readonly ReferenceValue[];
  readonly selectedTeams: readonly string[];
  readonly onSelectedTeamsChange: (next: readonly string[]) => void;
  readonly statuses: readonly ReferenceValue[];
  readonly selectedStatuses: readonly string[];
  readonly onSelectedStatusesChange: (next: readonly string[]) => void;
  readonly onClear: () => void;
}

const F = strings.projects.filters;

function selectedValues(event: ChangeEvent<HTMLSelectElement>): string[] {
  return Array.from(event.target.selectedOptions).map((option) => option.value);
}

export function ProjectFilters({
  nameContains,
  onNameContainsChange,
  teams,
  selectedTeams,
  onSelectedTeamsChange,
  statuses,
  selectedStatuses,
  onSelectedStatusesChange,
  onClear,
}: ProjectFiltersProps) {
  const hasActiveFilters = nameContains.trim().length > 0 || selectedTeams.length > 0 || selectedStatuses.length > 0;

  return (
    <div className="eg-project-filters">
      <FormField id="eg-project-filter-name" label={F.name}>
        <input
          type="text"
          value={nameContains}
          onChange={(event) => onNameContainsChange(event.target.value)}
          {...formFieldControlProps("eg-project-filter-name")}
        />
      </FormField>

      <FormField id="eg-project-filter-team" label={F.team} description={F.multiSelectHint}>
        <select
          multiple
          value={[...selectedTeams]}
          onChange={(event) => onSelectedTeamsChange(selectedValues(event))}
          {...formFieldControlProps("eg-project-filter-team", F.multiSelectHint)}
        >
          {teams.map((team) => (
            <option key={team.key} value={team.code}>
              {team.canonicalLabel}
            </option>
          ))}
        </select>
      </FormField>

      <FormField id="eg-project-filter-status" label={F.status} description={F.multiSelectHint}>
        <select
          multiple
          value={[...selectedStatuses]}
          onChange={(event) => onSelectedStatusesChange(selectedValues(event))}
          {...formFieldControlProps("eg-project-filter-status", F.multiSelectHint)}
        >
          {statuses.map((status) => (
            <option key={status.key} value={status.code}>
              {status.canonicalLabel}
            </option>
          ))}
        </select>
      </FormField>

      {/* Reserves the same label-row height as the FormField-wrapped controls beside it (see
         projects.css), so the button lines up with the input/selects rather than their labels. */}
      <div className="eg-project-filters__clear-wrap">
        <span aria-hidden="true">&nbsp;</span>
        <button type="button" className="eg-project-filters__clear" disabled={!hasActiveFilters} onClick={onClear}>
          {F.clear}
        </button>
      </div>
    </div>
  );
}
